import React from "react";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { tokens } from "../../theme/tokens";
import {
  WorkstreamName,
  WorkstreamGroupKey,
  TargetStatus,
  TargetType,
  TaskType,
  TaskTemplate,
} from "./types";

export const WORKSTREAM_ORDER: WorkstreamName[] = [
  "LOCATE & CONTACT",
  "RELATIVES / ASSOCIATES",
  "ONLINE / SOCIAL",
  "DATA PROVIDERS",
  "FIELD RESOURCES",
  "RECORDS",
  "CUSTOM",
];

export const PRIMARY_COLOR = tokens.color.text.primary;

export const STATUS_COLUMN_ORDER: TargetStatus[] = ["NEW", "DOING", "WORKED", "BAD"];

export const TASK_STATUS_COLOR: Record<string, string> = {
  TODO: tokens.color.workflow.new.fg,
  DOING: tokens.color.workflow.doing.fg,
  DONE: tokens.color.workflow.worked.fg,
  BLOCKED: tokens.color.workflow.bad.fg,
};

export const TYPE_CONFIG: Record<
  TargetType,
  { label: string; Icon: React.ElementType }
> = {
  PHONE: { label: "Phone", Icon: PhoneIcon },
  EMAIL: { label: "Email", Icon: EmailIcon },
  ADDRESS: { label: "Address", Icon: LocationOnIcon },
  PROFILE: { label: "Profile", Icon: LanguageIcon },
  PERSON: { label: "Person", Icon: PersonIcon },
  FIELD_RESOURCE: { label: "Resource", Icon: AssignmentIndIcon },
  OTHER: { label: "Other", Icon: RadioButtonUncheckedIcon },
};

export const STATUS_CONFIG: Record<
  TargetStatus,
  { label: string; bg: string; color: string; border: string }
> = {
  NEW: {
    label: "New",
    bg: tokens.color.workflow.new.bg,
    color: tokens.color.workflow.new.fg,
    border: tokens.color.workflow.new.border,
  },
  DOING: {
    label: "Doing",
    bg: tokens.color.workflow.doing.bg,
    color: tokens.color.workflow.doing.fg,
    border: tokens.color.workflow.doing.border,
  },
  BAD: {
    label: "Bad",
    bg: tokens.color.workflow.bad.bg,
    color: tokens.color.workflow.bad.fg,
    border: tokens.color.workflow.bad.border,
  },
  WORKED: {
    label: "Worked",
    bg: tokens.color.workflow.worked.bg,
    color: tokens.color.workflow.worked.fg,
    border: tokens.color.workflow.worked.border,
  },
};

// Derive task_type from group_key when task_type is not stored on the task (legacy API records).
export const GROUP_KEY_TO_TASK_TYPE: Partial<
  Record<WorkstreamGroupKey, TaskType>
> = {
  FIELD_RESOURCES: "FIELD_RESOURCES",
  DATA_PROVIDERS: "DATA_PROVIDER_SEARCH",
  ONLINE_SOCIAL: "SOCIAL_MEDIA_SEARCH",
  RELATIVES_ASSOCIATES: "ASSOCIATES_SEARCH",
  // LOCATE_CONTACT is ambiguous (phone or address) — falls back to CUSTOM
};

// Derive group_key and group_label from task_type.
export const TASK_TYPE_TO_GROUP: Record<
  TaskType,
  { group_key: WorkstreamGroupKey; group_label: WorkstreamName }
> = {
  PHONE_LOOKUP: {
    group_key: "LOCATE_CONTACT",
    group_label: "LOCATE & CONTACT",
  },
  EMAIL_SEARCH: {
    group_key: "LOCATE_CONTACT",
    group_label: "LOCATE & CONTACT",
  },
  ADDRESS_VERIFICATION: {
    group_key: "LOCATE_CONTACT",
    group_label: "LOCATE & CONTACT",
  },
  SOCIAL_MEDIA_SEARCH: {
    group_key: "ONLINE_SOCIAL",
    group_label: "ONLINE / SOCIAL",
  },
  INTERNET_SEARCH: {
    group_key: "ONLINE_SOCIAL",
    group_label: "ONLINE / SOCIAL",
  },
  DATA_PROVIDER_SEARCH: {
    group_key: "DATA_PROVIDERS",
    group_label: "DATA PROVIDERS",
  },
  ASSOCIATES_SEARCH: {
    group_key: "RELATIVES_ASSOCIATES",
    group_label: "RELATIVES / ASSOCIATES",
  },
  RELATIVES_SEARCH: {
    group_key: "RELATIVES_ASSOCIATES",
    group_label: "RELATIVES / ASSOCIATES",
  },
  FIELD_RESOURCES: {
    group_key: "FIELD_RESOURCES",
    group_label: "FIELD RESOURCES",
  },
  RECORDS_REQUEST: { group_key: "RECORDS", group_label: "RECORDS" },
  CUSTOM: { group_key: "CUSTOM", group_label: "CUSTOM" },
};

// Which target types are allowed for each task type.
// PasteTargetsModal reads this to filter the type selector.
// FIELD_RESOURCES tasks only allow FIELD_RESOURCE — persons and addresses are assigned
// within each field resource's value JSON, not as separate top-level targets.
export const TASK_TYPE_TARGET_MAP: Record<TaskType, TargetType[]> = {
  PHONE_LOOKUP: ["PHONE"],
  EMAIL_SEARCH: ["EMAIL"],
  ADDRESS_VERIFICATION: ["ADDRESS"],
  SOCIAL_MEDIA_SEARCH: ["PROFILE"],
  INTERNET_SEARCH: ["PROFILE", "ADDRESS"],
  DATA_PROVIDER_SEARCH: ["PERSON", "PHONE", "ADDRESS"],
  ASSOCIATES_SEARCH: ["PHONE", "EMAIL", "ADDRESS"],
  RELATIVES_SEARCH: ["PHONE", "EMAIL", "ADDRESS"],
  FIELD_RESOURCES: ["FIELD_RESOURCE"],
  RECORDS_REQUEST: ["OTHER", "PERSON", "ADDRESS"],
  CUSTOM: ["PHONE", "EMAIL", "ADDRESS", "PROFILE", "PERSON", "OTHER"],
};

export const TARGET_TYPE_LABELS: Record<TargetType, string> = {
  PHONE: "Phone Numbers",
  EMAIL: "Email Addresses",
  ADDRESS: "Addresses",
  PROFILE: "Social / Profile URLs",
  PERSON: "People",
  FIELD_RESOURCE: "Field Resource",
  OTHER: "Other",
};

export const CARD_SX = {
  backgroundColor: tokens.color.surface.default,
  border: `1px solid ${tokens.color.border.default}`,
  borderRadius: "10px",
  overflow: "hidden",
} as const;

// Task type catalog used by NewTaskSheet — kept here rather than dummyData.ts
// because these are real product constants, not test fixtures.
export const TASK_TEMPLATES: TaskTemplate[] = [
  { label: "Phone Lookup", task_type: "PHONE_LOOKUP" },
  { label: "Email Search", task_type: "EMAIL_SEARCH" },
  { label: "Address Check", task_type: "ADDRESS_VERIFICATION" },
  { label: "Social Media", task_type: "SOCIAL_MEDIA_SEARCH" },
  { label: "Internet Search", task_type: "INTERNET_SEARCH" },
  { label: "Data Provider", task_type: "DATA_PROVIDER_SEARCH" },
  { label: "Associates", task_type: "ASSOCIATES_SEARCH" },
  { label: "Relatives", task_type: "RELATIVES_SEARCH" },
  { label: "Field Resources", task_type: "FIELD_RESOURCES" },
  { label: "Records Request", task_type: "RECORDS_REQUEST" },
  { label: "Custom Task", task_type: "CUSTOM" },
];
